/**
 * Shared rules for the optional public product preview file.
 *
 * A preview is a free sample the seller attaches to a product: one file,
 * world-readable, shown on the product page to anyone, buyer or not. That
 * combination (public, unauthenticated, reachable from a link on the apex
 * domain) is the exact shape of an anonymous file-distribution service, so
 * every constraint below exists to stop it becoming one.
 *
 * There is NO download affordance for a preview, anywhere, in any format. The
 * bytes are only ever rendered inline: an <img>, a <video>, an <audio>, or a
 * cross-origin <iframe> for PDF. Nothing in this repo scans uploads
 * for malware, so a preview a visitor could save to disk would make supa.place
 * a malware host with a reputable domain in front of it. Rendering inline keeps
 * the blast radius inside the browser's own media and PDF parsers.
 *
 * The MIME allowlist is closed and short: every entry is a format the product
 * page has a renderer for. Anything else is refused three times over, at pick
 * time in the seller UI, by storage.rules on the upload itself, and again
 * server-side against the stored object's real metadata after the write. SVG is
 * excluded for the same reason it is excluded from cover and gallery uploads
 * (see ALLOWED_IMAGE_MIME_TYPES in src/lib/product-upload-limits.ts): it is an
 * XML and script bearer, not an image. Keep this list in sync with
 * isAllowedPreviewType() in storage.rules and with the mirrored copy in
 * functions/src/lib/product-preview-validation.ts.
 *
 * PDF is the only framed type, and it is framed from the Firebase Storage
 * download URL, which is a different origin from supa.place. A hostile PDF that
 * breaks out of the viewer therefore lands in firebasestorage.googleapis.com's
 * origin, where it can reach neither our Firebase auth tokens nor our
 * IndexedDB. That cross-origin separation is the protection, and it is now the
 * whole of it: the iframe carries no sandbox attribute, because Chromium treats
 * its PDF viewer as a plugin and any sandbox at all makes Chrome and Edge
 * refuse to render the document. The element in
 * src/components/product/product-preview.tsx carries the full reasoning and the
 * list of what replaced the attribute, of which the closed content-type
 * allowlist below is the load-bearing part: it is what stops an HTML document,
 * the only thing that could run script in that frame, from ever being served
 * there.
 *
 * Nothing fetches preview bytes on pageload. The renderer is click-to-load,
 * because a 50MB asset on the most-visited page in the app must not be egress
 * on every view.
 *
 * Preview bytes deliberately do not count toward filesTotalBytes or the seller
 * tier cap. They are public marketing media, the same category as the cover and
 * gallery images, which are uncounted for the same reason.
 */

import { isOwnStorageUrl } from './storage-url';

// Ceiling for a single preview object. Mirrored by the 50MB literal in the
// preview block of storage.rules and by the constant in
// functions/src/lib/product-preview-validation.ts, which re-checks the real
// object size after upload. Inclusive: a file of exactly 52428800 bytes is
// accepted, matching the "Max 50MB" copy shown to sellers.
export const PREVIEW_MAX_FILE_SIZE = 50 * 1024 * 1024;

// The closed allowlist. Order matters only for PREVIEW_ACCEPT_ATTR below.
// Mirrors the alternation in storage.rules isAllowedPreviewType(): keep in sync.
export const PREVIEW_ALLOWED_MIME_TYPES = [
  'application/pdf',
  'video/mp4',
  'video/webm',
  'audio/mpeg',
  'audio/wav',
  'audio/x-wav',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
] as const;

export type PreviewKind = 'pdf' | 'video' | 'audio' | 'image';

// The stored shape of the `preview` field on a product document. Every field is
// required once the field exists at all, so a half-written preview resolves to
// nothing rather than to a section rendering a blank surface.
export type ProductPreview = {
  enabled: boolean;
  src: string;
  path: string;
  contentType: string;
  size: number;
  name: string;
};

// Which renderer a content type maps to. Exact string equality, never a prefix
// test: a prefix test on 'image/' would readmit image/svg+xml, and one on
// 'video/' would admit container formats no browser here can play.
const PREVIEW_KIND_BY_MIME: Record<string, PreviewKind> = {
  'application/pdf': 'pdf',
  'video/mp4': 'video',
  'video/webm': 'video',
  'audio/mpeg': 'audio',
  'audio/wav': 'audio',
  'audio/x-wav': 'audio',
  'image/jpeg': 'image',
  'image/png': 'image',
  'image/webp': 'image',
  'image/gif': 'image',
};

// The accept="" value for the seller's file input. This is a convenience hint
// only: every file picker offers an "all files" escape hatch, which is why
// isAllowedPreviewMime and sniffPreviewSignature still run on the picked file.
export const PREVIEW_ACCEPT_ATTR = PREVIEW_ALLOWED_MIME_TYPES.join(',');

export function isAllowedPreviewMime(type: string | undefined | null): boolean {
  return !!type && (PREVIEW_ALLOWED_MIME_TYPES as readonly string[]).includes(type);
}

export function previewKind(contentType: string | undefined | null): PreviewKind | null {
  if (!contentType) return null;
  return PREVIEW_KIND_BY_MIME[contentType] ?? null;
}

/**
 * Whether `src` addresses an object in one of OUR OWN storage buckets.
 *
 * The product page frames and plays `src` directly, and nothing in
 * firestore.rules pins the shape of a product document: the products block
 * validates named fields but has no keys().hasOnly(), so the owning seller can
 * write any `preview` map they like straight through the SDK, bypassing the
 * editor that produced this value.
 *
 * Checking the HOST is not enough, for the reason storage-url.ts already
 * documents: firebasestorage.googleapis.com fronts every bucket on Google
 * Cloud, so a host-only test would let a seller point the frame at an object in
 * a bucket they control and have supa.place render it. The CSP is no backstop
 * either, because frame-src and media-src allow that host wholesale. The bucket
 * is the part that has to match, which is what isOwnStorageUrl checks.
 *
 * enforceProductPreviewOrStrip in functions catches the same forgery and strips
 * the field, but it is a trigger: it runs after the write, so between the two
 * there is a window in which the page renders whatever the document currently
 * says, and a seller can keep re-writing to hold that window open. Refusing
 * here closes it, and costs one URL parse per render.
 */
/**
 * Whether `src` is a download URL for exactly the object at `path`.
 *
 * MIRRORS previewSrcAddressesObject in
 * functions/src/lib/product-preview-validation.ts, which is the binding copy
 * and carries the fuller reasoning. The short version: the page renders `src`,
 * not `path`, so validating the path alone leaves the rendered bytes unchecked
 * — nothing otherwise stops a document naming a harmless object it owns while
 * pointing src somewhere else entirely.
 *
 * Only the canonical `getDownloadURL` shape is accepted, matching the server.
 * The path-style and virtual-hosted storage URLs that `isOwnStorageUrl`
 * recognises really do address our bucket, but the CSP frame-src and media-src
 * lists carry only `firebasestorage.googleapis.com`, so a preview in either of
 * those shapes would pass every check we make and then be refused by the
 * browser as a blank frame. Refusing it here means the seller's own editor
 * preview shows the same nothing the buyer would get.
 */
function previewSrcAddressesPath(src: string, path: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(src);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:') return false;
  if (parsed.hostname.toLowerCase() !== 'firebasestorage.googleapis.com') return false;

  // /v0/b/<bucket>/o/<percent-encoded path>
  const segments = parsed.pathname.split('/').filter(Boolean);
  if (segments[0] !== 'v0' || segments[1] !== 'b' || segments[3] !== 'o') return false;

  const decode = (raw: string): string | null => {
    try {
      return decodeURIComponent(raw);
    } catch {
      // A malformed escape is not a path. decodeURIComponent throws on one, and
      // a URL carrying it parses perfectly happily, so this is the difference
      // between a rejected preview and an exception during render.
      return null;
    }
  };
  const bucket = decode(segments[2]);
  if (bucket === null || !OWN_PREVIEW_BUCKETS.includes(bucket.toLowerCase())) return false;

  // One segment when encoded the way the SDK encodes it, several when a
  // hand-written URL left the slashes literal. Join then decode so both
  // spellings of the same object compare equal.
  const object = decode(segments.slice(4).join('/'));
  return object !== null && object === path;
}

/**
 * The buckets a preview may live in. Mirrors OWN_STORAGE_BUCKETS in
 * src/lib/storage-url.ts and PREVIEW_STORAGE_BUCKETS in functions.
 */
const OWN_PREVIEW_BUCKETS: readonly string[] = [
  'studio-282433670-dd879.firebasestorage.app',
  'studio-282433670-dd879.appspot.com',
];

export function isPreviewSrcOnStorageHost(src: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(src);
  } catch {
    // Anything the URL parser rejects is not a download URL, including the
    // relative and scheme-relative forms and every javascript: payload that
    // failed to parse as one.
    return false;
  }
  return parsed.protocol === 'https:' && isOwnStorageUrl(parsed);
}

/**
 * Normalise an unknown document field into something renderable, or null.
 *
 * This is the SINGLE predicate the product page uses to decide whether a
 * preview section exists at all, so it is deliberately strict: the seller
 * toggle must be exactly true (not merely truthy), the src must be an https
 * URL on our own storage host (see isPreviewSrcOnStorageHost), and the content
 * type must be one this build has a renderer for. A
 * product written before this feature shipped, a preview the seller switched
 * off, and a preview whose type was later dropped from the allowlist all
 * collapse to null here and render nothing.
 *
 * Note that `enabled` is a DISPLAY control, not a privacy control. The storage
 * object stays world-readable at its download URL whatever this returns, and
 * only deleting the object revokes access. Do not write UI copy implying
 * otherwise.
 */
export function resolveProductPreview(raw: unknown, productId?: string): ProductPreview | null {
  if (!raw || typeof raw !== 'object') return null;
  const candidate = raw as Record<string, unknown>;
  if (candidate.enabled !== true) return null;

  const src = typeof candidate.src === 'string' ? candidate.src.trim() : '';
  if (!src) return null;
  // The src is framed and played as-is, and the document it came from is
  // seller-writable, so the host is checked here rather than trusted.
  if (!isPreviewSrcOnStorageHost(src)) return null;

  /**
   * The src must address THIS PRODUCT'S OWN preview object, not merely
   * something in our bucket.
   *
   * The host check above is a bucket check, which is what stops a preview
   * pointing at a stranger's storage. It says nothing about WHICH of our
   * objects is being rendered, and measured against the real code that gap was
   * wide: a document could name another product's preview, or a path under
   * `products/{id}/files/` — the paid deliverables — and this resolver
   * returned it happily. enforceProductPreviewOrStrip refuses all of those
   * server-side, so nothing survived a write for long, but a trigger runs
   * AFTER the write and a seller can keep re-writing to hold that window open.
   * The two checks are now the same check, which closes it.
   *
   * `productId` is optional so existing callers keep compiling, but the
   * product page passes it and that is the path this exists for. Without it
   * the src is still required to agree with the document's own `path`, which
   * on its own kills the deliverable-pointing case.
   */
  const declaredPath = typeof candidate.path === 'string' ? candidate.path : '';
  // The SHAPE is required whether or not the caller supplied a product id:
  // exactly `products/<id>/preview/<one segment>`. That single expression is
  // what rules out nesting, traversal out of the prefix, and — the case that
  // matters most — a path under `products/<id>/files/`, where the paid
  // deliverables live. A caller with no id still gets that guarantee.
  if (!/^products\/[^/]+\/preview\/[^/]+$/.test(declaredPath)) return null;
  // And when the id IS known, the prefix must be this product's own, so one
  // seller cannot surface another product's sample as their own.
  if (productId && !declaredPath.startsWith(`products/${productId}/preview/`)) return null;
  if (!previewSrcAddressesPath(src, declaredPath)) return null;

  const contentType = typeof candidate.contentType === 'string' ? candidate.contentType : '';
  if (!previewKind(contentType)) return null;

  const size = Number(candidate.size);

  return {
    enabled: true,
    src,
    path: declaredPath,
    contentType,
    size: Number.isFinite(size) && size > 0 ? size : 0,
    name: typeof candidate.name === 'string' ? candidate.name : '',
  };
}

// Signature tables. Built through ascii() wherever the signature really is text,
// so they read as the strings they are ('%PDF-', 'RIFF', 'ftyp') rather than as
// hex nobody can proofread.
function ascii(text: string): number[] {
  const bytes: number[] = [];
  for (let i = 0; i < text.length; i += 1) bytes.push(text.charCodeAt(i) & 0xff);
  return bytes;
}

const SIG_PDF = ascii('%PDF-');
const SIG_FTYP = ascii('ftyp');
const SIG_RIFF = ascii('RIFF');
const SIG_WAVE = ascii('WAVE');
const SIG_WEBP = ascii('WEBP');
const SIG_ID3 = ascii('ID3');
const SIG_GIF87A = ascii('GIF87a');
const SIG_GIF89A = ascii('GIF89a');
const SIG_WEBM = [0x1a, 0x45, 0xdf, 0xa3];
const SIG_PNG = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const SIG_JPEG = [0xff, 0xd8, 0xff];

// A short read is a failed match, never a throw. Callers hand us whatever the
// first slice of the file produced, which can be shorter than the signature
// under test (a tiny file, a truncated read, an empty File on some platforms).
// Out-of-range indexing into a Uint8Array yields undefined rather than raising,
// so this length guard is what turns a short buffer into a clean rejection
// instead of a comparison against undefined that happens to work today.
function matchesAt(head: Uint8Array, offset: number, signature: readonly number[]): boolean {
  if (!head || head.length < offset + signature.length) return false;
  for (let i = 0; i < signature.length; i += 1) {
    if (head[offset + i] !== signature[i]) return false;
  }
  return true;
}

/**
 * Magic-byte check on the first bytes of a picked file.
 *
 * File.type is the browser guessing from the extension, so it is trivially
 * forged: rename payload.html to sample.pdf and the resulting File claims
 * application/pdf, passes isAllowedPreviewMime, and would then be handed to the
 * framed PDF renderer. Reading the real container signature is what makes the
 * declared type mean anything at all.
 *
 * This is a consistency check, not a parser and not a virus scanner. It proves
 * the file starts the way its declared type must start and proves nothing about
 * the remaining bytes, so everything downstream still treats preview content as
 * hostile.
 *
 * A content type with no signature we know how to check returns false. Passing
 * requires a positive match, so widening PREVIEW_ALLOWED_MIME_TYPES without
 * adding a case here fails closed rather than open.
 */
export function sniffPreviewSignature(head: Uint8Array, contentType: string): boolean {
  if (!head || head.length === 0) return false;

  switch (contentType) {
    case 'application/pdf':
      // Some real-world PDFs carry junk ahead of the header and readers tolerate
      // it, but this renderer frames the file rather than repairing it, so a
      // preview that does not start with the header is refused.
      return matchesAt(head, 0, SIG_PDF);

    case 'video/mp4':
      // ISO base media format: bytes 0 to 3 are the box size, then the 'ftyp'
      // box type. The brand that follows varies far too much across encoders to
      // be worth allowlisting.
      return matchesAt(head, 4, SIG_FTYP);

    case 'video/webm':
      // EBML header, shared with Matroska. The DocType that distinguishes webm
      // from matroska sits further in than the head we are given.
      return matchesAt(head, 0, SIG_WEBM);

    case 'audio/wav':
    case 'audio/x-wav':
      // RIFF container with a WAVE form type at byte 8. Testing only 'RIFF'
      // would also accept WebP and AVI, which is why both halves are checked.
      return matchesAt(head, 0, SIG_RIFF) && matchesAt(head, 8, SIG_WAVE);

    case 'audio/mpeg':
      // Either an ID3v2 tag or a bare MPEG audio frame. The frame sync is eleven
      // set bits, so the second byte is 0xEx or 0xFx: accepting 0xEx as well
      // admits MPEG-2.5, which is rare but legitimate at low bitrates and would
      // otherwise be rejected as a forgery.
      if (matchesAt(head, 0, SIG_ID3)) return true;
      return head.length >= 2 && head[0] === 0xff && (head[1] & 0xe0) === 0xe0;

    case 'image/jpeg':
      return matchesAt(head, 0, SIG_JPEG);

    case 'image/png':
      return matchesAt(head, 0, SIG_PNG);

    case 'image/webp':
      // Also a RIFF container, distinguished from WAV by its form type.
      return matchesAt(head, 0, SIG_RIFF) && matchesAt(head, 8, SIG_WEBP);

    case 'image/gif':
      return matchesAt(head, 0, SIG_GIF87A) || matchesAt(head, 0, SIG_GIF89A);

    default:
      return false;
  }
}
